import {
  DIESEL_TANK_KINDS,
  PROPANE_TANK_STATUSES,
  SURVEY_FUELS,
  SURVEY_SECTION_FIELDS,
  SYSTEM_CHANGES,
  type ProposalSection,
  type SurveyFuel,
} from '../../types';

/** The proposal section's field names, in form order. */
export const PROPOSAL_FIELD_NAMES: readonly (keyof ProposalSection)[] = SURVEY_SECTION_FIELDS.proposal;

/** Same cap the server applies to every section string. */
export const MAX_PROPOSAL_TEXT_LENGTH = 2000;

const oneOf =
  (allowed: readonly string[]) =>
  (v: unknown): boolean =>
    typeof v === 'string' && allowed.includes(v);

// Blank is "not asked yet", which is a null (clear), never a stored empty or whitespace-only string.
const text = (v: unknown): boolean =>
  typeof v === 'string' && v.trim() !== '' && v.length <= MAX_PROPOSAL_TEXT_LENGTH;
const TEXT_EXPECTED = `text of 1 to ${MAX_PROPOSAL_TEXT_LENGTH} characters (send null to clear)`;

const change = { ok: oneOf(SYSTEM_CHANGES), expected: `one of ${SYSTEM_CHANGES.join(', ')}` };

const RULES: Record<keyof ProposalSection, { ok: (v: unknown) => boolean; expected: string }> = {
  proposedFuel: { ok: oneOf(SURVEY_FUELS), expected: `one of ${SURVEY_FUELS.join(', ')}` },
  fuelSupplyChange: change,
  propaneTankStatus: {
    ok: oneOf(PROPANE_TANK_STATUSES),
    expected: `one of ${PROPANE_TANK_STATUSES.join(', ')}`,
  },
  propaneTankLocation: { ok: text, expected: TEXT_EXPECTED },
  dieselTankKind: { ok: oneOf(DIESEL_TANK_KINDS), expected: `one of ${DIESEL_TANK_KINDS.join(', ')}` },
  dieselTankLocation: { ok: text, expected: TEXT_EXPECTED },
  panelChange: change,
  panelChangeNotes: { ok: text, expected: TEXT_EXPECTED },
  transferSwitchExisting: { ok: text, expected: TEXT_EXPECTED },
  transferSwitchChange: change,
  transferSwitchProposed: { ok: text, expected: TEXT_EXPECTED },
  padChange: change,
};

/**
 * Why a value cannot be stored in a proposal field, or null when it can.
 * A null is never a valid stored value (clearing is the caller's decision),
 * so it is refused like any other wrong type.
 */
export function proposalFieldError(field: string, value: unknown): string | null {
  if (!Object.prototype.hasOwnProperty.call(RULES, field)) {
    return `${field} is not a proposal field`;
  }
  const rule = RULES[field as keyof ProposalSection];
  return rule.ok(value) ? null : `${field} must be ${rule.expected}`;
}

/**
 * The fuel a survey is for. A survey with no choice (every survey taken before
 * this section existed) reads as natural gas, which is what they all assumed.
 * A stored value that is not a known fuel is ignored the same way.
 */
export function effectiveProposedFuel(proposal: Pick<ProposalSection, 'proposedFuel'> | null | undefined): SurveyFuel {
  const stored = proposal?.proposedFuel;
  return stored && (SURVEY_FUELS as readonly string[]).includes(stored) ? stored : 'natural_gas';
}

/**
 * The fuel the surveyor actually chose, or null when none was (never asked, cleared, or not a known
 * fuel). `effectiveProposedFuel` answers "which questions apply" (null reads as natural gas); this answers
 * "is a fuel stored", which is not the same as "did a person choose it": AI writes carry no provenance,
 * so a stored value may not come from a human. A reader that must not guess (the permit kit: ambiguous
 * means ask) uses this one.
 */
export function chosenProposedFuel(proposal: Pick<ProposalSection, 'proposedFuel'> | null | undefined): SurveyFuel | null {
  const stored = proposal?.proposedFuel;
  return stored && (SURVEY_FUELS as readonly string[]).includes(stored) ? stored : null;
}

export interface FuelQuestions {
  gasMeter: boolean;
  propaneTank: boolean;
  dieselTank: boolean;
}

/**
 * Which fuel-specific questions apply. Readers key off the CURRENT fuel only, so
 * propane answers left behind after switching to diesel are ignored, never shown.
 */
export function proposalFieldsForFuel(fuel: SurveyFuel): FuelQuestions {
  return {
    gasMeter: fuel === 'natural_gas',
    propaneTank: fuel === 'propane',
    dieselTank: fuel === 'diesel',
  };
}
